import { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import moment from "moment";
import { Icon } from "@iconify/react";
import useWindowDimensions from "../Hooks/useWindowDimensions";

import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";

import "./TicketFieldsForm.css";
import Loader from "./Loader";

import {
  getAllMaster,
  getChannelList,
  getCustomInput,
} from "../Store/Actions/TicketAction";

const TicketFieldsForm = ({
  customInput,
  formData,
  isAttachmentTooLarge,
  isBulkEditing,
  isCustomInputLoading,
  isDescriptionEmpty,

  // For useEffect dependency to show loader when opening merged ticket
  isMergedTicket,

  isTicketFieldsLoading,
  isUpdating,
  setAttachment,
  setCustomInput,
  setFormData,
  setIsAttachmentTooLarge,
  setIsCustomInputLoading,
  setIsDescriptionEmpty,
  setIsTicketFieldsLoading,
  singleColumn,
}) => {
  const [channelList, setChannelList] = useState([]);
  const [masterData, setMasterData] = useState({});
  const [reminderTime, setReminderTime] = useState("");
  const [isReminderExpired, setIsReminderExpired] = useState(false);
  const { width } = useWindowDimensions();

  useEffect(() => {
    setIsTicketFieldsLoading(true);

    getChannelList()
      .then((response) => {
        if (response.status) {
          setChannelList(response.data);
        } else {
          console.log("error", response.error);
        }
      })
      .catch((err) => {
        console.log("err", err);
      });

    getAllMaster()
      .then((response) => {
        if (response.status) {
          process.env.NODE_ENV === "development" &&
            console.log("master data", response.data);

          setMasterData(response.data);
          setIsTicketFieldsLoading(false);
        } else {
          console.log("error", response.error);
          setIsTicketFieldsLoading(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsTicketFieldsLoading(false);
      });
  }, [isMergedTicket]);

  // Countdown reminder
  useEffect(() => {
    let interval;

    if (formData.reminder) {
      // Get countdown target
      let countDownDate = moment(formData.reminder);

      interval = setInterval(function () {
        // Get differences in time and day
        let diff = countDownDate.diff(moment());
        let diffDays = countDownDate.diff(moment(), "days");

        // When the timer expires, change the countdown to a regular timer and set error state
        if (diff <= 0) {
          setIsReminderExpired(true);
          diff = moment().diff(countDownDate);
        }

        // Show the value
        setReminderTime(
          `${diffDays} days ${moment.utc(diff).format("HH:mm:ss")}`
        );
      }, 1000);
    }

    // Clear interval and reset error state when component is unmounted
    return () => {
      clearInterval(interval);
      setIsReminderExpired(false);
    };
  }, [formData.reminder]);

  // Modules and formats for React Quill component
  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
  ];

  const renderCustomFields = () => {
    let renderedCustomFields = [];
    let customValues = [];

    /*
      This function will be called every time the custom input value changes. The value of the current index in customValues will be updated, and then set to update the casecontrolvalue object in the formData state.
    */
    const handleChange = (index, value) => {
      customValues[index].value = value;

      setFormData({
        ...formData,
        casecontrolvalue: customValues,
      });
    };

    /*
      For each custom input, push a new object into customValues array. Set the label, then set the value if it has one. Else, set it to empty.
    */
    customInput?.forEach((field, index) => {
      customValues.push({
        controlname: field.ControlName,
        value: formData.casecontrolvalue
          ? formData.casecontrolvalue[index]?.value
          : "",
      });

      switch (field.ControlType) {
        case "DATE":
          renderedCustomFields.push(
            <Grid
              item
              key={index}
              xs={singleColumn ? 12 : width <= 780 ? 12 : 6}
            >
              <TextField
                defaultValue={field.Value}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  readOnly: isUpdating && formData.isfrontlinernoteditable,
                }}
                label={field.Label}
                required={!isBulkEditing}
                type="datetime-local"
                value={formData.casecontrolvalue[index]?.value}
                onChange={(e) => handleChange(index, e.target.value)}
              />
            </Grid>
          );

          break;

        case "DROPDOWN":
          renderedCustomFields.push(
            <Grid
              item
              key={index}
              xs={singleColumn ? 12 : width <= 780 ? 12 : 6}
            >
              <TextField
                defaultValue={field.Value}
                fullWidth
                InputProps={{
                  readOnly: isUpdating && formData.isfrontlinernoteditable,
                }}
                label={field.Label}
                required={!isBulkEditing}
                select
                value={formData.casecontrolvalue[index]?.value}
                onChange={(e) => handleChange(index, e.target.value)}
              >
                {field.ValueList?.split("#").map((value) => (
                  <MenuItem value={value}>{value}</MenuItem>
                ))}
              </TextField>
            </Grid>
          );
          break;

        case "TEXTBOX":
          renderedCustomFields.push(
            <Grid
              item
              key={index}
              xs={singleColumn ? 12 : width <= 780 ? 12 : 6}
            >
              <TextField
                defaultValue={field.Value}
                fullWidth
                label={field.Label}
                required={!isBulkEditing}
                value={formData.casecontrolvalue[index]?.value}
                InputProps={{
                  endAdornment: !formData.isfrontlinernoteditable && (
                    <InputAdornment
                      position="end"
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleChange(index, "")}
                    >
                      <Icon icon="ph:x-bold" width="26" />
                    </InputAdornment>
                  ),

                  readOnly: isUpdating && formData.isfrontlinernoteditable,
                }}
                onChange={(e) => handleChange(index, e.target.value)}
              />
            </Grid>
          );

          break;

        default:
          break;
      }
    });

    return renderedCustomFields;
  };

  return isTicketFieldsLoading ? (
    <Loader />
  ) : (
    <section className="ticket-fields-form form-inputs">
      <Grid
        className="grid-container"
        columnSpacing={"12px"}
        container
        rowSpacing={"14px"}
      >
        {isUpdating && (
          <>
            {
              // Hide when ticket status is closed
              formData.statusid !== 1 && (
                <Grid item xs={singleColumn ? 12 : width <= 780 ? 12 : 6}>
                  <TextField
                    className="reminder"
                    disabled={!reminderTime}
                    error={reminderTime && isReminderExpired}
                    fullWidth
                    inputProps={{ readOnly: true }}
                    label="Reminder Time"
                    value={
                      reminderTime ? reminderTime : "Reminder time is not set"
                    }
                  />
                </Grid>
              )
            }

            <Grid item xs={singleColumn ? 12 : width <= 780 ? 12 : 6}>
              <TextField
                fullWidth
                inputProps={{ readOnly: true }}
                label="Ticket No"
                value={formData.ticketno}
                InputProps={{
                  endAdornment: !formData.isfrontlinernoteditable && (
                    <InputAdornment
                      position="end"
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        navigator.clipboard.writeText(formData.ticketno)
                      } // Copy text to clipboard
                    >
                      <Tooltip arrow title="Copy value">
                        <Icon
                          icon="material-symbols:content-copy"
                          height="28"
                          width="28"
                        />
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </>
        )}

        <Grid
          className={`group-separator ${singleColumn ? "compact" : ""}`}
          item
          xs={12}
        >
          <Button variant="outlined" disabled>
            Contact Information
          </Button>
          <hr />
        </Grid>

        <Grid item xs={singleColumn ? 12 : width <= 780 ? 12 : 6}>
          <TextField
            fullWidth
            label="Phone No"
            value={formData.phoneno}
            InputProps={{
              endAdornment: !formData.isfrontlinernoteditable && (
                <InputAdornment
                  position="end"
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    setFormData({
                      ...formData,
                      phoneno: "",
                    })
                  }
                >
                  <Icon icon="ph:x-bold" width="26" />
                </InputAdornment>
              ),

              readOnly: isUpdating && formData.isfrontlinernoteditable,
            }}
            onChange={(e) =>
              setFormData({
                ...formData,
                phoneno: e.target.value,
              })
            }
          />
        </Grid>

        <Grid item xs={singleColumn ? 12 : width <= 780 ? 12 : 6}>
          <TextField
            fullWidth
            label="E-mail"
            value={formData.email}
            InputProps={{
              endAdornment: !formData.isfrontlinernoteditable && (
                <InputAdornment
                  position="end"
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    setFormData({
                      ...formData,
                      email: "",
                    })
                  }
                >
                  <Icon icon="ph:x-bold" width="26" />
                </InputAdornment>
              ),

              readOnly: isUpdating && formData.isfrontlinernoteditable,
            }}
            onChange={(e) =>
              setFormData({
                ...formData,
                email: e.target.value,
              })
            }
          />
        </Grid>

        <Grid
          className={`group-separator ${singleColumn ? "compact" : ""}`}
          item
          xs={12}
        >
          <Button variant="outlined" disabled>
            Ticket Information
          </Button>

          <hr />
        </Grid>

        {isUpdating && (
          <Grid item xs={singleColumn ? 12 : width <= 780 ? 12 : 6}>
            <TextField
              fullWidth
              label="Title"
              required={!isBulkEditing}
              value={formData.title}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  title: e.target.value,
                })
              }
            />
          </Grid>
        )}

        <Grid item xs={singleColumn ? 12 : width <= 780 ? 12 : 6}>
          <TextField
            fullWidth
            inputProps={{ readOnly: isUpdating }}
            label="Channel"
            required={!isBulkEditing}
            select
            value={formData.contactid}
            onChange={(e) =>
              setFormData({
                ...formData,
                contactid: e.target.value,
              })
            }
          >
            {/* {masterData.master_contact.map((contact) => (
              <MenuItem value={contact.ContactID}>
                {contact.Description}
              </MenuItem>
            ))} */}

            {channelList.map((channel) => (
              <MenuItem value={channel.ChannelID}>
                {channel.Description}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {isUpdating && formData.assignto && (
          <Grid item xs={singleColumn ? 12 : width <= 780 ? 12 : 6}>
            <TextField
              fullWidth
              label="Assign to"
              value={formData.assignto}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        )}

        <Grid item xs={singleColumn ? 12 : width <= 780 ? 12 : 6}>
          <TextField
            fullWidth
            InputProps={{
              readOnly: isUpdating && formData.isfrontlinernoteditable,
            }}
            label="Priority"
            required={!isBulkEditing}
            select
            value={formData.priorityid}
            onChange={(e) =>
              setFormData({
                ...formData,
                priorityid: e.target.value,
              })
            }
          >
            {masterData.master_priority.map((priority) => (
              <MenuItem value={priority.PriorityID}>
                {priority.Description}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={singleColumn ? 12 : width <= 780 ? 12 : 6}>
          <TextField
            fullWidth
            InputProps={{
              readOnly: isUpdating && formData.isfrontlinernoteditable,
            }}
            label="Category"
            required={!isBulkEditing}
            select
            value={formData.typeid}
            onChange={(e) => {
              setFormData({
                ...formData,
                typeid: e.target.value,
                subtypeid: null,
                subsubtypeid: null,
                casecontrolvalue: [],
                statusid: "",
              });

              setCustomInput([]);
            }}
          >
            {masterData.master_type
              ?.filter((parent) => parent.ParentID === 0)
              .map((type) => (
                <MenuItem value={type.TypeID}>{type.Description}</MenuItem>
              ))}
          </TextField>
        </Grid>

        {formData.typeid &&
        masterData.master_type?.filter(
          (parent) => parent.ParentID === formData.typeid
        ).length > 0 ? (
          <Grid item xs={singleColumn ? 12 : width <= 780 ? 12 : 6}>
            <TextField
              fullWidth
              InputProps={{
                readOnly: isUpdating && formData.isfrontlinernoteditable,
              }}
              label="Sub Category"
              required={!isBulkEditing}
              select
              value={formData.subtypeid}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  subtypeid: e.target.value,
                  subsubtypeid: null,
                  casecontrolvalue: [],
                });

                setCustomInput([]);
              }}
            >
              {formData.typeid ? (
                masterData.master_type
                  ?.filter((parent) => parent.ParentID === formData.typeid)
                  .map((type) => (
                    <MenuItem value={type.TypeID}>{type.Description}</MenuItem>
                  ))
              ) : (
                <MenuItem value="">Please select a reason type</MenuItem>
              )}
            </TextField>
          </Grid>
        ) : (
          ""
        )}

        {formData.subtypeid &&
        masterData.master_type?.filter(
          (parent) => parent.ParentID === formData.subtypeid
        ).length > 0 ? (
          <Grid item xs={singleColumn ? 12 : width <= 780 ? 12 : 6}>
            <TextField
              fullWidth
              InputProps={{
                readOnly: isUpdating && formData.isfrontlinernoteditable,
              }}
              label="Detail Sub Category"
              required={!isBulkEditing}
              select
              value={formData.subsubtypeid}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  subsubtypeid: e.target.value,
                  casecontrolvalue: [],
                });

                setCustomInput([]);

                getCustomInput(e.target.value)
                  .then((response) => {
                    if (response.status) {
                      console.log("Custom input: ", response.data);
                      setCustomInput(response.data);
                      setIsCustomInputLoading(false);
                    } else {
                      console.log("error", response.error);
                      setIsCustomInputLoading(false);
                    }
                  })
                  .catch((err) => {
                    console.log("err", err);
                    setIsCustomInputLoading(false);
                  });
              }}
            >
              {formData.subtypeid ? (
                masterData.master_type
                  ?.filter((parent) => parent.ParentID === formData.subtypeid)
                  .map((type) => (
                    <MenuItem value={type.TypeID}>{type.Description}</MenuItem>
                  ))
              ) : (
                <MenuItem value="">Please select a reason</MenuItem>
              )}
            </TextField>
          </Grid>
        ) : (
          ""
        )}

        <Grid
          className="show-in-bulk-edit"
          item
          xs={singleColumn ? 12 : width <= 780 ? 12 : 6}
        >
          <TextField
            fullWidth
            InputProps={{
              readOnly: isUpdating && formData.isfrontlinernoteditable,
            }}
            label="Status"
            required
            select
            value={formData.statusid}
            onChange={(e) =>
              setFormData({
                ...formData,
                statusid: e.target.value,
              })
            }
          >
            {/* {masterData.master_status.map((status) => (
              <MenuItem value={status.StatusID}>{status.Description}</MenuItem>
            ))} */}

            {/*
              Condition to check status
              1. If masterData.master_status only shows two options (meaning the agent is in frontliner department) Then only show the "New" status option
              2. If masterData.master_status only shows two options (meaning the agent is in frontliner department) and category "Inquiry" is selected, then show "New" and "Closed" status option
              3. Otherwise show all status options
            */}

            {(() => {
              const statusList = masterData.master_status || [];

              if (masterData.IsFrontliner && formData.typeid !== 2) {
                return statusList.filter((status) => status.StatusID === 2);
              }

              return statusList;
            })().map((status) => (
              <MenuItem key={status.StatusID} value={status.StatusID}>
                {status.Description}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={singleColumn ? 12 : width <= 780 ? 12 : 6}>
          <TextField
            fullWidth
            InputProps={{
              readOnly: isUpdating && formData.isfrontlinernoteditable,
            }}
            label="Department"
            required={!isBulkEditing}
            select
            value={formData.departmentid}
            onChange={(e) =>
              setFormData({
                ...formData,
                departmentid: e.target.value,

                department: masterData.master_departement?.find(
                  (department) => department.DepartmentID === e.target.value
                ).Description,
              })
            }
          >
            {masterData.master_departement?.map((department) => (
              <MenuItem value={department.DepartmentID}>
                {department.Description}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {formData.statusid === 1 && (
          <Grid
            className="show-in-bulk-edit"
            item
            xs={singleColumn ? 12 : width <= 780 ? 12 : 6}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.issendcsat}
                  readOnly={isUpdating && formData.isfrontlinernoteditable}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      issendcsat: e.target.checked,
                    })
                  }
                />
              }
              label="Send Email Rating"
            />
          </Grid>
        )}

        {/* Only show when status is closed */}
        {formData.statusid === 1 && (
          <Grid
            className="show-in-bulk-edit"
            item
            xs={singleColumn ? 12 : width <= 780 ? 12 : 6}
          >
            <TextField
              fullWidth
              label="Resolution"
              minRows={3}
              multiline
              required
              value={formData.resolution}
              InputProps={{
                endAdornment: !formData.isfrontlinernoteditable && (
                  <InputAdornment
                    position="end"
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      setFormData({
                        ...formData,
                        resolution: "",
                      })
                    }
                  >
                    <Icon icon="ph:x-bold" width="26" />
                  </InputAdornment>
                ),

                readOnly: isUpdating && formData.isfrontlinernoteditable,
              }}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  resolution: e.target.value,
                })
              }
            />
          </Grid>
        )}

        {customInput?.length > 0 && (
          <>
            {isCustomInputLoading ? (
              <Loader />
            ) : (
              <>
                <Grid
                  className={`group-separator ${singleColumn ? "compact" : ""}`}
                  item
                  xs={12}
                >
                  <Button variant="outlined" disabled>
                    Custom Field
                  </Button>
                  <hr />
                </Grid>

                {renderCustomFields()}
              </>
            )}
          </>
        )}

        {!isUpdating && (
          <>
            <Grid item style={{ paddingTop: 0 }} xs={12} />

            <Grid className="quill-container" item xs={6}>
              <ReactQuill
                className={isDescriptionEmpty ? "error" : ""}
                formats={formats}
                modules={modules}
                placeholder="Description *"
                theme="snow"
                value={formData.description}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    description: e,
                  });

                  setIsDescriptionEmpty(false);
                }}
              />

              {isDescriptionEmpty && (
                <div className="required-message">
                  <Icon
                    color="#ffb529"
                    icon="ion:warning"
                    width="20"
                    height="20"
                  />
                  <p>Please fill out this field.</p>
                </div>
              )}
            </Grid>

            <Grid className="quill-container" item xs={6}>
              <ReactQuill
                formats={formats}
                modules={modules}
                placeholder="Internal Notes"
                theme="snow"
                value={formData.remark}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    remark: e,
                  });

                  setIsDescriptionEmpty(false);
                }}
              />
            </Grid>

            <Grid item xs={singleColumn ? 12 : width <= 780 ? 12 : 6}>
              {isAttachmentTooLarge && (
                <div className="required-message">
                  <Icon
                    color="#ffb529"
                    icon="ion:warning"
                    width="20"
                    height="20"
                  />
                  <p>Attachment file size cannot exceed 25 MB!</p>
                </div>
              )}

              <TextField
                className="input-attachment"
                error={isAttachmentTooLarge}
                fullWidth
                label="Attachments"
                type="file"
                InputLabelProps={{ shrink: true }}
                inputProps={{ multiple: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Icon icon="mdi:attachment-vertical" width="26" />
                    </InputAdornment>
                  ),
                }}
                onChange={async (e) => {
                  const filesToUpload = []; // For upload API, includes base64 string
                  const filesToAttach = []; // For insertticket API, without base64
                  setIsAttachmentTooLarge(false);

                  try {
                    for (let i = 0; i < e.target.files.length; i++) {
                      const file = e.target.files[i];
                      const reader = new FileReader();
                      reader.readAsDataURL(file);

                      reader.onload = () => {
                        filesToUpload.push({
                          name: file.name,
                          filename: file.name,
                          contenttype: file.type,
                          file: reader.result,
                        });

                        filesToAttach.push({
                          name: file.name,
                          filename: file.name,
                          contenttype: file.type,
                          bodylength: file.size,
                        });
                      };
                    }
                  } catch (error) {
                    console.log("error upload: ", error);
                  }

                  setFormData({
                    ...formData,
                    attachment: filesToAttach,
                  });

                  setAttachment(filesToUpload);
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </section>
  );
};

export default TicketFieldsForm;
